import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { loadProgress } from "../state/progress";
import { useToast } from "../hooks/use-toast";
import { Layers3, LayoutDashboard, Settings, HeartHandshake, NotebookPen, Menu, Share2, MessagesSquare } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import MobileNav from "./MobileNav";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { isDiaryAvailable } from "../lib/availability";
import { FORUM_ENABLED } from "../lib/features";
import "../styles/hud.css";

function NavItem({ to, children, dataTourId }) {
  return (
    <NavLink
      to={to}
      data-tour-id={dataTourId}
      className={({ isActive }) => `nav-link px-2 py-1 text-sm ${isActive ? "" : "text-slate-600"}`}
      children={({ isActive }) => (
        <span data-active={isActive ? "true" : "false"}>{children}</span>
      )}
    />
  );
}

export default function NavBar() {
  const nav = useNavigate();
  const { toast } = useToast();

  const handleContinue = () => {
    const s = loadProgress();
    if (s.currentWeek === 0) nav("/week/1");
    else nav(`/week/${s.currentWeek}`);
  };

  const showDiary = isDiaryAvailable();

  return (
    <div data-tour-id="navbar" className="sticky top-0 z-40 w-full navbar-surface">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2" data-tour-id="navbar-left">
          <div className="md:hidden mobile-only"><MobileNav /></div>
          <button className="flex items-center gap-2 font-semibold tracking-tight focus-brand" onClick={() => nav("/")} aria-label="Go to Home">
            <Layers3 className="h-5 w-5" style={{ color: "var(--brand-700)" }} />
            <span className="text-slate-900 dark:text-slate-100">Somemas Recovery</span>
          </button>
        </div>
        <div className="hidden md:flex desktop-nav items-center gap-2">
          <NavItem dataTourId="nav-home" to="/">Home</NavItem>
          <Button data-tour-id="nav-tracker" variant="ghost" className="text-sm" onClick={() => nav("/tracker")} aria-label="Open tracker">
            <LayoutDashboard className="mr-2 h-4 w-4" /> Tracker
          </Button>
          <Button data-tour-id="nav-sponsors" variant="ghost" className="text-sm" onClick={() => nav("/sponsors")} aria-label="Open sponsors">
            <HeartHandshake className="mr-2 h-4 w-4" /> Our Sponsors
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-tour-id="nav-settings" variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => nav("/settings")}><Settings className="mr-2 h-4 w-4" /> Settings</DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/refer")}><Share2 className="mr-2 h-4 w-4" /> Refer Others</DropdownMenuItem>
              {FORUM_ENABLED && <DropdownMenuItem onClick={() => nav("/forum")} data-testid="nav-forum-dropdown-item"><MessagesSquare className="mr-2 h-4 w-4" /> Forum</DropdownMenuItem>}
              {showDiary && <DropdownMenuItem onClick={() => nav("/diary")}><NotebookPen className="mr-2 h-4 w-4" /> Diary</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/privacy")}>Privacy Policy</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}